import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useContributorGroup } from '@/features/contributorGroups/useContributorGroup'
import useConfig from '@/features/configs/useConfig'
import { ApprovalStatus } from '@/features/offers/constants'
import { authSelector } from '@/stores/auth/authSlice'
import { paddingZeroString } from '@/utils/stringHelper'
import { VStack } from '@chakra-ui/react'
import {
  BookingApproverRole,
  CostBudgetType,
  OfferApprovalType,
  PRApprovalType,
  PRStatus,
  requiresHSP1Approval,
} from '@eapproval/offer-shared'
import { useExchangeRate } from '@/utils/exchangeRate.helper'
import { useMemo } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import PRQuotationDetailSection from './PRQuotationDetailSection'
import PRSubmission from '../PRSubmission'
const PRVendorDetailTab = ({ costBudgetIndex, vendorSelectionIndex }) => {
  const { user } = useSelector(authSelector)

  const { setValue, getValues } = useFormContext()
  const costBudgetType = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.type` })

  const { useFetchAllContributorGroups } = useContributorGroup()
  const { useFetchConfigByType } = useConfig()

  const { data: contributorGroupsResponse } = useFetchAllContributorGroups({ page: 1, pageSize: 0 })
  const { data: logisticUserConfig } = useFetchConfigByType('OFFER_LOGISTIC_USER')
  const { data: hsp1UserConfig } = useFetchConfigByType('OFFER_HSP1_USER')
  const { data: exchangeRateMap } = useExchangeRate()

  const defaultProcurementOfficer = useMemo(() => {
    if (!Array.isArray(contributorGroupsResponse?.data)) {
      return null
    }

    const procurementOfficerGroup = contributorGroupsResponse?.data.find(
      (group) => group.type === ContributorGroupType.PROCUREMENT_OFFICER
    )

    if (!procurementOfficerGroup) return null

    return procurementOfficerGroup?.defaultUser?._id
  }, [contributorGroupsResponse])

  const {
    fields: PRSubmissions,
    append: appendPRSubmission,
    remove: removePRSubmission,
  } = useFieldArray({
    name: `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions`,
  })

  const defaultLogisticUser = logisticUserConfig?.mainValue || null
  const defaultHSP1User = hsp1UserConfig?.mainValue || null

  const handleAddPRSubmission = (requestedDetails = [], requestorId = '') => {
    const securityOfPayment = getValues('securityOfPayment')
    const RFQCode = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.RFQCode`)
    const exceedThreshold = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail.exceedThreshold`)
    const contributors = getValues('contributors')
    const bookingApprovers = getValues('booking.approverDetails') ?? []
    const projectManager = getValues('booking.projectManager')
    const procurementOfficer = getValues('sourcing.procurementOfficer')

    const currency = getValues(
      `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.currency`
    )

    const totalAmount = requestedDetails.reduce((acc, row) => {
      const quantity = row?.quantity ?? 0
      const unitRate = row?.unitRate ?? 0
      const discount = row?.discount ?? 0

      const lumpSumDiscount = row?.lumpSumDiscount ?? 0

      const amount = quantity * (unitRate * (1 - discount / 100) - lumpSumDiscount)
      return acc + amount
    }, 0)

    const hasMissingSAPMaterialNumber = requestedDetails.some((row) => !String(row?.SAPMaterialNumber ?? '').trim())

    const CPMDetail = bookingApprovers.find((detail) => detail.type === BookingApproverRole.COMMERCIAL_PROJECT_MANAGER)

    const technical3Detail = bookingApprovers.find(
      (detail) => detail.type === BookingApproverRole.TECHNICAL && detail.level === 3
    )
    const commercial3Detail = bookingApprovers.find(
      (detail) => detail.type === BookingApproverRole.COMMERCIAL && detail.level === 3
    )

    const approverDetails = []

    approverDetails.push({
      status: user?._id !== projectManager ? ApprovalStatus.UPCOMING : ApprovalStatus.APPROVED,
      user: projectManager,
      type: PRApprovalType.TECHNICAL_PROJECT_MANAGER,
      dueDate: null,
      remark: '',
      attachments: [],
    })
    approverDetails.push({
      status: ApprovalStatus.UPCOMING,
      user: procurementOfficer,
      type: PRApprovalType.HSP_2,
      dueDate: null,
      remark: '',
      attachments: [],
    })
    const needsHSP1Approval = requiresHSP1Approval({
      amount: totalAmount,
      currency: currency,
      exchangeRateToHKD: exchangeRateMap?.[currency],
      threshold: getValues('sourcing.sourcingThreshold'),
    })

    if (needsHSP1Approval) {
      approverDetails.push({
        status: ApprovalStatus.UPCOMING,
        user: defaultHSP1User ?? null,
        type: PRApprovalType.HSP_1,
        dueDate: null,
        remark: '',
        attachments: [],
      })
    }
    approverDetails.push({
      status: ApprovalStatus.UPCOMING,
      user: technical3Detail?.user?._id,
      type: PRApprovalType.TECHNICAL_3,
      dueDate: null,
      remark: '',
      attachments: [],
    })
    if (hasMissingSAPMaterialNumber) {
      approverDetails.push({
        status: ApprovalStatus.UPCOMING,
        user: defaultLogisticUser ?? null,
        type: PRApprovalType.LOGISTIC,
        dueDate: null,
        remark: '',
        attachments: [],
      })
    }

    approverDetails.push({
      status: ApprovalStatus.UPCOMING,
      user: CPMDetail?.user?._id,
      type: PRApprovalType.COMMERCIAL_PROJECT_MANAGER,
      dueDate: null,
      remark: '',
      attachments: [],
    })

    approverDetails.push({
      status: ApprovalStatus.UPCOMING,
      user: commercial3Detail?.user?._id,
      type: PRApprovalType.COMMERCIAL_3,
      dueDate: null,
      remark: '',
      attachments: [],
    })
    approverDetails.push({
      status: ApprovalStatus.UPCOMING,
      user: defaultLogisticUser ?? null,
      type: PRApprovalType.LOGISTIC,
      dueDate: null,
      remark: '',
      attachments: [],
    })
    appendPRSubmission({
      raiser: user._id,
      requestor: requestorId || user._id,
      PRNumber: `${RFQCode}-V${vendorSelectionIndex + 1}-PR${paddingZeroString(PRSubmissions.length + 1)}`,
      PONumber: '',
      totalAmount: totalAmount,
      descriptionOfProduct: '',
      licenseRequired: false,
      expectedDeliveryDate: undefined,
      deliveryInstructions: '',
      internalDocument: [],
      securityOfPayment: securityOfPayment ?? false,
      status: PRStatus.DRAFT,
      requiredWorkInstructionAttachments: [],
      requiredWorkInstructionRemark: '',
      requestedDetails: requestedDetails,
      approverDetails: approverDetails,
      subcontractorAgreementRequired: costBudgetType === CostBudgetType.SUPPLY_MAINTENANCE_SERVICE,
      currency: currency,
    })
  }

  const handleUpdatePRSubmission = (requestedDetails = []) => {
    if (PRSubmissions.length === 0) {
      toast.error('Please add PR submission first')
      return
    }

    setValue(
      `sourcing.costBudgets.${costBudgetIndex}.PR.vendorSelections.${vendorSelectionIndex}.submissions.${PRSubmissions.length - 1}.requestedDetails`,
      requestedDetails,
      { shouldDirt: true }
    )
  }
  return (
    <>
      <PRQuotationDetailSection
        costBudgetIndex={costBudgetIndex}
        vendorSelectionIndex={vendorSelectionIndex}
        handleAddPRSubmission={handleAddPRSubmission}
        handleUpdatePRSubmission={handleUpdatePRSubmission}
      />
      <VStack gap="2">
        {PRSubmissions.slice()
          .reverse()
          .map((submission, submissionIndex) => (
            <PRSubmission
              key={submission.id}
              costBudgetIndex={costBudgetIndex}
              vendorSelectionIndex={vendorSelectionIndex}
              submissionIndex={PRSubmissions.length - 1 - submissionIndex}
              removePRSubmission={removePRSubmission}
            />
          ))}
      </VStack>
    </>
  )
}
export default PRVendorDetailTab
